<script setup>
import ImportTrackedKeywordsAction from '@frontJs/components/actions/projectManagement/seo/ImportTrackedKeywordsAction.vue';
import UpdateTrackedKeywordAction from '@frontJs/components/actions/projectManagement/seo/UpdateTrackedKeywordAction.vue';
import TrackedKeywordImportantToFollowCell from '@frontJs/components/tables/cells/projectManagement/seo/TrackedKeywordImportantToFollowCell.vue';
import EyeIcon from '@iconify-vue/ph/eye';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import ContainerRoot from '@js/components/containers/ContainerRoot.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h, ref } from 'vue';

const { tenant, countries } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
    selectData,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'searchTerm',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mot-clé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.searchTerm,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'countOfSeoTrackedKeywords',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mot-clé' + '<br/>' + 'Ajouté',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.countOfSeoTrackedKeywords > 0,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'position',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Position',
                    align: 'right',
                }),
            cell: (props) =>
                h(
                    NumberCell,
                    {
                        number: props.row.original.position,
                        align: 'right',
                    },
                    props.row.original.positionChartData
                        ? {
                              actions: () =>
                                  h(DisplayLineChartAction, {
                                      labels: props.row.original
                                          .positionChartData.labels,
                                      datasets: [
                                          {
                                              group: 'Position',
                                              label: 'Position',
                                              backgroundColor: '#4d65b9',
                                              borderColor: '#4d65b9',
                                              pointRadius: 0,
                                              pointHoverRadius: 0,
                                              fill: {
                                                  target: 'start',
                                                  above: '#e4e8f4',
                                              },
                                              tension: 0.4,
                                              data: props.row.original
                                                  .positionChartData.data,
                                              labels: props.row.original
                                                  .positionChartData.labels,
                                              yAxisID: 'y',
                                          },
                                      ],
                                      title: 'Positions',
                                      withDecimalsAxis1: false,
                                      reverseAxis1: true,
                                      minAxis1: 1,
                                      maxAxis1: 100,
                                  }),
                          }
                        : {},
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'searchVolume',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Volume',
                    align: 'right',
                }),
            cell: (props) =>
                props.row.original.searchVolume
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.searchVolume,
                              align: 'right',
                          },
                          props.row.original.searchVolumeChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .searchVolumeChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Volume de recherche',
                                                    label: 'Volume de recherche',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .searchVolumeChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .searchVolumeChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Volumes de recherche',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
        }),
        columnHelper.display({
            id: 'url',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'URL',
                }),
            cell: (innerProps) =>
                innerProps.row.original.url
                    ? h(BaseCell, {}, [
                          h(ActionButton, {
                              icon: EyeIcon,
                              size: 'small',
                              type: 'secondary',
                              square: true,
                              title: 'Voir',
                              href: innerProps.row.original.url,
                              target: '_blank',
                          }),
                      ])
                    : null,
            width: 0,
        }),
        columnHelper.accessor(() => null, {
            id: 'seoTrackedKeywordImportantToFollow',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Important',
                }),
            cell: (props) =>
                props.row.original.countOfSeoTrackedKeywords
                    ? h(TrackedKeywordImportantToFollowCell, {
                          tenant,
                          seoTrackedKeyword:
                              props.row.original.seoTrackedKeyword,
                          callback: updateData,
                      })
                    : undefined,
        }),
        columnHelper.accessor(() => null, {
            id: 'sumOfMetricsClicks',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.sumOfMetricsClicks,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sumOfMetricsImpressions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Impressions',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.sumOfMetricsImpressions,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sumOfMetricsConversions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Conversions',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.sumOfMetricsConversions,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sumOfMetricsConversionsValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur conversions',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.sumOfMetricsConversionsValue,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.countOfSeoTrackedKeywords === 0
                                ? h(
                                      ImportTrackedKeywordsAction,
                                      {
                                          tenant,
                                          callback: updateData,
                                          keywords:
                                              props.row.original.searchTerm,
                                          disabledKeywords: true,
                                      },
                                      {
                                          button: ({ setIsOpen }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Importer',
                                                  callback: () =>
                                                      setIsOpen(true),
                                                  icon: UploadSimpleIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.countOfSeoTrackedKeywords > 0
                                ? h(
                                      UpdateTrackedKeywordAction,
                                      {
                                          tenant,
                                          countries,
                                          seoTrackedKeyword:
                                              props.row.original
                                                  .seoTrackedKeyword,
                                          callback: updateData,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  icon: PencilSimpleIcon,
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Modifier',
                                                  type: 'primary',
                                              }),
                                      },
                                  )
                                : null,
                        ],
                    },
                ),
        }),
    ],
    url: route(
        'front.projectManagement.seo.postGoogleAdsCampaignSearchTermViews',
        {
            tenant: tenant.id,
        },
    ),
    exportUrl: route(
        'front.projectManagement.seo.postExportGoogleAdsCampaignSearchTermViews',
        {
            tenant: tenant.id,
        },
    ),
    columnPinning: {
        right: ['actions'],
    },
    selectionIdKey: 'searchTerm',
});

const importLoading = ref(false);

const keywords = ref('');

function importAction(setIsOpen, setKeywords) {
    importLoading.value = true;

    selectData()
        .then((data) => {
            setKeywords('');
            keywords.value = '';

            data.forEach((item) => {
                if (item.seoTrackedKeyword === null) {
                    keywords.value += item.searchTerm + '\n';
                }
            });

            if (keywords.value === '') {
                toastError('Aucun mot-clé à importer.');
            } else {
                setKeywords(keywords.value);
                setIsOpen(true);
            }
        })
        .finally(() => {
            importLoading.value = false;
        });
}
</script>

<template>
    <ContainerRoot class="wysiwyg mb-4">
        <ul>
            <li>Mots-clés relevés par Google Ads sur les 12 derniers mois</li>
        </ul>
    </ContainerRoot>

    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ImportTrackedKeywordsAction
                :tenant="tenant"
                :callback="updateData"
                :keywords="keywords"
                disabled-keywords
            >
                <template #button="{ setIsOpen, setKeywords }">
                    <ActionButton
                        size="small"
                        title="Importer"
                        :callback="() => importAction(setIsOpen, setKeywords)"
                        :left-icon="UploadSimpleIcon"
                        :disabled="selectionIds.length === 0 || importLoading"
                        :loading="importLoading"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportTrackedKeywordsAction>
        </template>
    </DataTableLayout>
</template>
